import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAtom } from "jotai/index";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { AAutocompleteElement } from "@/components/a-autocomplete";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { isOwner } from "@/lib/auth-predicates";
import { isValidPort, trimSplitCsvRecord } from "@/lib/common-utils";
import { meStore } from "@/store/store";

type AddingMode = "one" | "list" | "csv";
type CsvDelimiter = "," | ";";

const socks5AddressRegExp = /socks5:\/\/\S+:\S+@\S+:\d+/gm;
const anySymbolBesidesSpaceSymbolsRegExp = /^\S+$/;
const entireStringMatchingIpAddressRegExp =
  /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

type Props = {
  open: boolean;
  title?: string;
  confirmLoading?: boolean;
  onCancel?: () => void;
  onSave?: (authGroups: string[], value: string[]) => void;
};

export const ProxyAddModal: React.FC<Props> = ({
  open,
  title,
  confirmLoading = false,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);

  const [addingMode, setAddingMode] = useState<AddingMode>("one");
  const [csvDelimiter, setCsvDelimiter] = useState<CsvDelimiter>(",");

  const { data: authGroupsRs } = useAuthGroupsQuery();

  // react-hook-form
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    trigger,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      authGroups: [] as string[],
      one: "",
      list: "",
      csv: "",
    },
    mode: "onChange",
  });

  // if only one authGroup and user is not owner — set that group
  useEffect(() => {
    if (authGroupsRs?.data?.length === 1 && !isOwner(me)) {
      setValue(
        "authGroups",
        authGroupsRs.data.map((it: any) => it.name)
      );
    }
  }, [authGroupsRs, setValue, me]);

  // authGroups options
  const authGroupsOptions = useMemo(
    () =>
      authGroupsRs?.data
        ?.map((i: any) => ({ label: i.name, value: i.name }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)) ?? [],
    [authGroupsRs?.data]
  );

  // ---------- validation helpers (from your original code) ----------
  function validateProxyCsvRecord(record: string): string | undefined {
    const values = trimSplitCsvRecord(record, csvDelimiter);

    if (values.length != 4) {
      return t("recordMustHaveValues");
    }

    if (!anySymbolBesidesSpaceSymbolsRegExp.test(values[0])) {
      return t("usernameNotValid");
    }

    if (!anySymbolBesidesSpaceSymbolsRegExp.test(values[1])) {
      return t("passwordNotValid");
    }

    if (!entireStringMatchingIpAddressRegExp.test(values[2])) {
      return t("ipAddressNotValid");
    }

    if (!isValidPort(values[3])) {
      return t("portNotValid");
    }

    return undefined;
  }

  function proxyListValidator(value: any) {
    if (!value || !value.trim()) return t("proxyAddressesRequired");

    if (typeof value !== "string") return t("proxyAddressMustBeString");

    if (!value.trim()) return t("proxyAddressesRequired");

    // check regexp matches at least once
    const match = value.match(socks5AddressRegExp);
    if (match === null) {
      return t("noStatementsMatchPattern", {
        pattern: socks5AddressRegExp.toString(),
      });
    }

    return true;
  }

  function proxyCsvValidator(value: any) {
    if (!value) return t("proxiesRequired");

    if (typeof value !== "string") return t("proxiesMustBeString");

    if (!value.trim()) return t("proxiesRequired");

    const records = value.split("\n");
    for (const record of records) {
      const validationMessage = validateProxyCsvRecord(record);
      if (validationMessage !== undefined)
        return t("invalidRecordInInput", {
          validationMessage,
          record,
        });
    }

    return true;
  }

  function convertCsvRecordToProxyAddress(record: string): string {
    const values = trimSplitCsvRecord(record, csvDelimiter);
    return `socks5://${values[0]}:${values[1]}@${values[2]}:${values[3]}`;
  }

  function convertCsvInputToProxyAddress(input: string) {
    const records = input.split("\n");
    return records.map((item) => convertCsvRecordToProxyAddress(item)).join("\n");
  }

  // ---------- handlers ----------
  function handleChangeAddingMode(value: AddingMode) {
    setAddingMode(value);
    // reset field values related to modes
    setValue("one", "");
    setValue("list", "");
    setValue("csv", "");
  }

  function handleChangeDelimiter(value: CsvDelimiter) {
    setCsvDelimiter(value);
  }

  async function handleSaveClick() {
    // run react-hook-form validation for fields we care about
    // validate authGroups always, and the current mode field
    type FieldName = "authGroups" | "one" | "list" | "csv";
    const toValidate: FieldName[] = ["authGroups"];
    if (addingMode === "one") toValidate.push("one");
    if (addingMode === "list") toValidate.push("list");
    if (addingMode === "csv") toValidate.push("csv");

    const ok = await trigger(toValidate);
    if (!ok) return;

    // now manual/regex checks as in original
    const authGroups: string[] = getValues("authGroups") ?? [];
    let value: string = getValues(addingMode) ?? "";

    if (addingMode === "csv") value = convertCsvInputToProxyAddress(value);

    const match = value.match(socks5AddressRegExp);
    if (match === null) {
      // show error (you used antd.message before) — use alert + console.error
      console.error(
        "Something went wrong while saving config — array of matching regexp is null or undefined"
      );
      // better UX: show user a message
      window.alert(
        "array of matching by regexp pattern is null or undefined" /*t("somethingWentWrongWhileSavingConfig")*/
      );
      return;
    }

    const regExpMatchesArray: string[] = match;

    if (onSave) onSave(authGroups, regExpMatchesArray);
  }

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        )
          return; //ask question if changes==true and click outSide
        onCancel?.();
      }}
    >
      <DialogTitle>
        {title ? (
          <Typography variant="h6" sx={{ marginTop: 1 }}>
            {title}
          </Typography>
        ) : (
          <div
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography variant="h6">{t("addProxies")}</Typography>
            <IconButton onClick={() => onCancel && onCancel()}>
              <CloseIcon />
            </IconButton>
          </div>
        )}
      </DialogTitle>

      {title && <Divider sx={{ my: 3 }} />}

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* adding mode radio */}
          <div>
            <Typography variant="subtitle1">{t("chooseAddingType")}</Typography>
            <RadioGroup
              row
              value={addingMode}
              onChange={(e) => handleChangeAddingMode(e.target.value as AddingMode)}
            >
              <FormControlLabel value="one" control={<Radio />} label={t("one")} />
              <FormControlLabel value="list" control={<Radio />} label={t("list")} />
              <FormControlLabel value="csv" control={<Radio />} label={t("csv")} />
            </RadioGroup>
          </div>

          <Divider />

          {/* form fields */}
          <Stack spacing={2}>
            {/* authGroups */}
            <AAutocompleteElement
              name="authGroups"
              control={control}
              label={t("authGroups")}
              multiple
              options={authGroupsOptions.map((x) => x.value)}
              textFieldProps={{
                variant: "outlined",
                error: !!(errors as any).authGroups?.message,
                helperText: (errors as any).authGroups?.message ?? " ",
              }}
              //disabled={authGroupsRs?.data?.length === 1 && !isOwner(me)}
              // make it required unless owner, but react-hook-form rules applied below via Controller would be complex;
              // We rely on trigger() and manual check same as original: original required: !isOwner(me)
            />

            {/* one */}
            {addingMode === "one" && (
              <Controller
                name="one"
                control={control}
                rules={{
                  required: t("proxyAddressRequired"),
                  pattern: {
                    value: socks5AddressRegExp,
                    message: t("proxyAddressMustMatchPattern"),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("proxyAddress")}
                    placeholder={t("typeFullProxyAddress")}
                    fullWidth
                    error={!!(errors as any).one}
                    helperText={(errors as any).one?.message ?? " "}
                  />
                )}
              />
            )}

            {/* list */}
            {addingMode === "list" && (
              <Controller
                name="list"
                control={control}
                rules={{
                  required: t("proxyAddressesRequired"),
                  validate: (v) => {
                    const res = proxyListValidator(v);
                    return res === true ? true : res;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("proxyAddresses")}
                    placeholder="socks5://{username}:{password}@{ip}:{port}"
                    fullWidth
                    multiline
                    minRows={8}
                    error={!!(errors as any).list}
                    helperText={(errors as any).list?.message ?? " "}
                  />
                )}
              />
            )}

            {/* csv */}
            {addingMode === "csv" && (
              <>
                <div>
                  <Typography variant="subtitle2">{t("delimiter")}</Typography>
                  <RadioGroup
                    row
                    value={csvDelimiter}
                    onChange={(e) => handleChangeDelimiter(e.target.value as CsvDelimiter)}
                  >
                    <FormControlLabel value="," control={<Radio />} label="," />
                    <FormControlLabel value=";" control={<Radio />} label=";" />
                  </RadioGroup>
                </div>

                <Controller
                  name="csv"
                  control={control}
                  rules={{
                    required: t("proxiesRequired"),
                    validate: (v) => {
                      // value is string
                      const res = proxyCsvValidator(v);
                      return res === true ? true : res;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("proxies")}
                      placeholder={`username${csvDelimiter}password${csvDelimiter}ip${csvDelimiter}port`}
                      fullWidth
                      multiline
                      minRows={8}
                      error={!!(errors as any).csv}
                      helperText={(errors as any).csv?.message ?? " "}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
              </>
            )}

            {/* end of form fields */}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <ModalActionsButtons
          onCancelClick={() => onCancel && onCancel()}
          onConfirmClick={async () => {
            await handleSaveClick();
          }}
          loading={confirmLoading}
        />
        {/*<Button onClick={() => onCancel && onCancel()} disabled={confirmLoading}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={async () => {
            await handleSaveClick();
          }}
          disabled={confirmLoading}
        >
          {confirmLoading ? t("loading") : t("submit")}
        </Button>*/}
      </DialogActions>
    </Dialog>
  );
};

export default ProxyAddModal;
